import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Edit, Save, X, RefreshCw, MinusCircle } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { DeductionCategory, loadDeductionCategories } from '@/lib/settingsConfig';

const DEFAULT_TENANT_ID = '1225f4cb-43ae-463d-a6b0-53f721362dbf';

const makeKey = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

type PayoutMode = 'none' | 'company_wide' | 'individual';

interface FormState {
  id?: string;
  category_key: string;
  label: string;
  is_insurance: boolean;
  affects_net: boolean;
  sort_order: string;
  is_active: boolean;
  payout_mode: PayoutMode;
  bank_account_number: string;
  bank_account_name: string;
}

const emptyForm = (): FormState => ({
  category_key: '',
  label: '',
  is_insurance: false,
  affects_net: true,
  sort_order: '100',
  is_active: true,
  payout_mode: 'none',
  bank_account_number: '',
  bank_account_name: '',
});

const PAYOUT_MODE_OPTIONS: { value: PayoutMode; title: string; hint: string }[] = [
  { value: 'none', title: 'Reduces net pay only', hint: "No separate banking line — just comes off the driver's pay." },
  { value: 'company_wide', title: 'Company-wide account', hint: 'Every driver pays the same shared account below (e.g. the Excess Fund).' },
  { value: 'individual', title: 'Individual (set per driver)', hint: "New deductions under this category default to “Bank account required” ticked -- an admin can still untick it per deduction if that particular one just repays the company instead." },
];

const DeductionCategoriesManager: React.FC = () => {
  const [categories, setCategories] = useState<DeductionCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm());

  const load = async () => {
    setLoading(true);
    const data = await loadDeductionCategories(DEFAULT_TENANT_ID);
    setCategories(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const startAdd = () => {
    setForm(emptyForm());
    setShowForm(true);
  };

  const startEdit = (cat: DeductionCategory) => {
    setForm({
      id: cat.id,
      category_key: cat.category_key,
      label: cat.label,
      is_insurance: cat.is_insurance,
      affects_net: cat.affects_net,
      sort_order: String(cat.sort_order ?? 100),
      is_active: cat.is_active,
      payout_mode: cat.payout_mode || 'none',
      bank_account_number: cat.bank_account_number || '',
      bank_account_name: cat.bank_account_name || '',
    });
    setShowForm(true);
  };

  const update = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'label' && !prev.id) next.category_key = makeKey(String(value));
      return next;
    });
  };

  const save = async () => {
    if (!form.label.trim()) {
      toast({ title: 'Name required', description: 'Please enter a category name.', variant: 'destructive' });
      return;
    }
    const key = makeKey(form.category_key || form.label);
    if (!key) {
      toast({ title: 'Key required', description: 'A simple internal key is needed.', variant: 'destructive' });
      return;
    }

    setSaving(true);
    const payload = {
      tenant_id: DEFAULT_TENANT_ID,
      category_key: key,
      label: form.label.trim(),
      is_insurance: form.is_insurance,
      affects_net: form.affects_net,
      sort_order: Number(form.sort_order) || 100,
      is_active: form.is_active,
      payout_mode: form.payout_mode,
      // Only a company-wide category carries its own account -- clear any
      // stale value if the mode was switched away from it.
      bank_account_number: form.payout_mode === 'company_wide' ? form.bank_account_number.trim() || null : null,
      bank_account_name: form.payout_mode === 'company_wide' ? form.bank_account_name.trim() || null : null,
    };

    const query = form.id
      ? supabase.from('deduction_categories').update(payload).eq('id', form.id).select()
      : supabase.from('deduction_categories').insert([payload]).select();

    const { error } = await query;
    setSaving(false);

    if (error) {
      toast({ title: 'Not saved', description: error.message, variant: 'destructive' });
      return;
    }

    toast({ title: 'Saved', description: `Deduction category "${payload.label}" saved.` });
    setShowForm(false);
    setForm(emptyForm());
    await load();
  };

  const toggleActive = async (cat: DeductionCategory) => {
    const { error } = await supabase
      .from('deduction_categories')
      .update({ is_active: !cat.is_active })
      .eq('id', cat.id);
    if (error) {
      toast({ title: 'Not updated', description: error.message, variant: 'destructive' });
      return;
    }
    await load();
  };

  const remove = async (cat: DeductionCategory) => {
    if (!window.confirm(`Delete deduction category "${cat.label}"?\n\nExisting driver deductions already using it keep their value, but it will no longer be selectable.`)) {
      return;
    }
    const { error } = await supabase.from('deduction_categories').delete().eq('id', cat.id);
    if (error) {
      toast({ title: 'Not deleted', description: error.message, variant: 'destructive' });
      return;
    }
    await load();
  };

  return (
    <Card className="shadow-lg bg-card border border-border">
      <CardHeader className="border-b border-border">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="text-xl text-primary flex items-center gap-2">
              <MinusCircle className="w-5 h-5" />
              Deduction Categories
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Categories drivers can be charged under (Insurance, Structured Repayment, Manual, etc.).
              These appear in the driver Deductions form — no code changes needed.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load} disabled={loading}>
              <RefreshCw className="w-4 h-4 mr-2" />
              {loading ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button onClick={startAdd} className="golden-button">
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 p-5">
        {showForm && (
          <div className="rounded-xl border border-yellow-900/30 bg-black/20 p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-foreground">
                {form.id ? 'Edit Category' : 'Add Category'}
              </h3>
              <Button type="button" variant="ghost" size="sm" onClick={() => setShowForm(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-yellow-500">Name *</Label>
                <Input value={form.label} onChange={(e) => update('label', e.target.value)} placeholder="e.g. Insurance" />
              </div>
              <div>
                <Label className="text-yellow-500">Internal Key</Label>
                <Input value={form.category_key} onChange={(e) => update('category_key', e.target.value)} placeholder="e.g. insurance" />
                <p className="mt-1 text-xs text-muted-foreground">Lowercase with underscores.</p>
              </div>
              <div>
                <Label className="text-yellow-500">Sort Order</Label>
                <Input type="number" value={form.sort_order} onChange={(e) => update('sort_order', e.target.value)} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="flex items-center gap-2 rounded-lg border border-border bg-background/40 p-3 text-sm">
                <Checkbox checked={form.is_insurance} onCheckedChange={(c) => update('is_insurance', Boolean(c))} />
                <span>Insurance category (uses weekly insurance amount)</span>
              </label>
              <label className="flex items-center gap-2 rounded-lg border border-border bg-background/40 p-3 text-sm">
                <Checkbox checked={form.affects_net} onCheckedChange={(c) => update('affects_net', Boolean(c))} />
                <span>Affects net pay</span>
              </label>
              <label className="flex items-center gap-2 rounded-lg border border-border bg-background/40 p-3 text-sm">
                <Checkbox checked={form.is_active} onCheckedChange={(c) => update('is_active', Boolean(c))} />
                <span>Active</span>
              </label>
            </div>

            <div className="rounded-lg border border-border bg-background/40 p-3 space-y-3">
              <Label className="text-yellow-500">Payout</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {PAYOUT_MODE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => update('payout_mode', opt.value)}
                    className={`rounded-lg border p-3 text-left text-sm transition-colors ${
                      form.payout_mode === opt.value
                        ? 'border-yellow-500 bg-yellow-500/10'
                        : 'border-border bg-background/40 hover:border-yellow-500/40'
                    }`}
                  >
                    <div className="font-medium text-foreground">{opt.title}</div>
                    <div className="mt-1 text-xs text-muted-foreground">{opt.hint}</div>
                  </button>
                ))}
              </div>

              {form.payout_mode === 'company_wide' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
                  <div>
                    <Label className="text-yellow-500">Bank Account Number</Label>
                    <Input
                      value={form.bank_account_number}
                      onChange={(e) => update('bank_account_number', e.target.value)}
                      placeholder="e.g. 15-3953-0028610-01"
                    />
                  </div>
                  <div>
                    <Label className="text-yellow-500">Bank Account Name</Label>
                    <Input
                      value={form.bank_account_name}
                      onChange={(e) => update('bank_account_name', e.target.value)}
                      placeholder="e.g. NZI Insurance Excess"
                    />
                  </div>
                </div>
              )}

              {form.payout_mode === 'individual' && (
                <p className="pt-1 text-xs text-muted-foreground">
                  When this deduction is added to a driver, an admin enters that driver's own bank account,
                  account name and a reference/case number on their Deductions tab.
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="golden-button">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Category'}
              </Button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="border-b border-border">
                <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">Category</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">Key</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">Flags</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">Status</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">Loading...</td></tr>
              ) : categories.length === 0 ? (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">No deduction categories yet. Add one to get started.</td></tr>
              ) : (
                categories.map((cat) => (
                  <tr key={cat.id} className={`border-b border-border last:border-b-0 ${!cat.is_active ? 'opacity-60' : ''}`}>
                    <td className="px-3 py-3 font-semibold text-foreground">{cat.label}</td>
                    <td className="px-3 py-3 text-xs text-muted-foreground">{cat.category_key}</td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        {cat.is_insurance && <Badge variant="outline" className="text-xs">Insurance</Badge>}
                        {cat.affects_net && <Badge variant="outline" className="text-xs">Affects net</Badge>}
                        {cat.payout_mode === 'company_wide' && cat.bank_account_number && (
                          <Badge variant="outline" className="text-xs text-green-400 border-green-700/50">
                            Pays to {cat.bank_account_name || 'account'}
                          </Badge>
                        )}
                        {cat.payout_mode === 'individual' && (
                          <Badge variant="outline" className="text-xs text-blue-400 border-blue-700/50">
                            Individual accounts
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <button
                        type="button"
                        onClick={() => toggleActive(cat)}
                        className={`rounded-full border px-2 py-1 text-xs font-medium ${
                          cat.is_active
                            ? 'border-green-700/50 bg-green-900/30 text-green-300'
                            : 'border-gray-700 bg-gray-900/40 text-gray-400'
                        }`}
                      >
                        {cat.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => startEdit(cat)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => remove(cat)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default DeductionCategoriesManager;
